import type { StateCreator } from 'zustand';
import type {
  FilterType,
  SecuritySelectionStore,
  SelectionMethod,
} from '../types';

export type DraftLeg = 'long' | 'short' | null;

export interface UISlice {
  isStepsExpanded: boolean;
  selectedFilterType: FilterType | null;
  selectionMethod: SelectionMethod;
  selectedCategory: string;
  metricsSearch: string;
  isAddingStep: boolean;
  editingMetricName: string | null;
  editingMetricId: string | null;
  editingStepId: string | null;
  activeDraftLeg: DraftLeg;
  toggleStepsExpanded: () => void;
  setSelectedFilterType: (type: FilterType | null) => void;
  setSelectionMethod: (method: SelectionMethod) => void;
  setSelectedCategory: (category: string) => void;
  setMetricsSearch: (search: string) => void;
  setIsAddingStep: (adding: boolean) => void;
  setEditingMetricName: (name: string | null) => void;
  setEditingMetric: (name: string | null, id: string | null) => void;
  setEditingStepId: (stepId: string | null) => void;
  setActiveDraftLeg: (leg: DraftLeg) => void;
  resetStepEditor: () => void;
}

export const createUISlice: StateCreator<
  SecuritySelectionStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  UISlice
> = (set) => ({
  isStepsExpanded: false,
  selectedFilterType: null,
  selectionMethod: 'fixed',
  selectedCategory: 'all',
  metricsSearch: '',
  isAddingStep: false,
  editingMetricName: null,
  editingMetricId: null,
  editingStepId: null,
  activeDraftLeg: null,

  toggleStepsExpanded: () =>
    set(
      (state) => {
        state.isStepsExpanded = !state.isStepsExpanded;
      },
      false,
      'ui/toggleStepsExpanded',
    ),

  setSelectedFilterType: (type) =>
    set(
      (state) => {
        state.selectedFilterType = type;
      },
      false,
      'ui/setSelectedFilterType',
    ),

  setSelectionMethod: (method) =>
    set(
      (state) => {
        state.selectionMethod = method;
      },
      false,
      'ui/setSelectionMethod',
    ),

  setSelectedCategory: (category) =>
    set(
      (state) => {
        state.selectedCategory = category;
      },
      false,
      'ui/setSelectedCategory',
    ),

  setMetricsSearch: (search) =>
    set(
      (state) => {
        state.metricsSearch = search;
      },
      false,
      'ui/setMetricsSearch',
    ),

  setIsAddingStep: (adding) =>
    set(
      (state) => {
        state.isAddingStep = adding;
        if (!adding) {
          state.selectedFilterType = null;
          state.selectionMethod = 'fixed';
          state.selectedCategory = 'all';
          state.metricsSearch = '';
        }
      },
      false,
      'ui/setIsAddingStep',
    ),

  setEditingMetricName: (name) =>
    set(
      (state) => {
        state.editingMetricName = name;
      },
      false,
      'ui/setEditingMetricName',
    ),

  setEditingMetric: (name, id) =>
    set(
      (state) => {
        state.editingMetricName = name;
        state.editingMetricId = id;
      },
      false,
      'ui/setEditingMetric',
    ),

  setEditingStepId: (stepId) =>
    set(
      (state) => {
        state.editingStepId = stepId;
      },
      false,
      'ui/setEditingStepId',
    ),

  setActiveDraftLeg: (leg) =>
    set(
      (state) => {
        state.activeDraftLeg = leg;
      },
      false,
      'ui/setActiveDraftLeg',
    ),

  resetStepEditor: () =>
    set(
      (state) => {
        state.selectedFilterType = null;
        state.selectionMethod = 'fixed';
        state.selectedCategory = 'all';
        state.metricsSearch = '';
        state.isAddingStep = false;
        state.editingMetricName = null;
        state.editingMetricId = null;
        state.editingStepId = null;
        state.activeDraftLeg = null;
      },
      false,
      'ui/resetStepEditor',
    ),
});
